// Explains, in full, what reversing a transaction will move. A reversal always
// undoes the whole transaction, so the confirmation must not describe a
// single statement line (such as a fee) as if only that line were reversed.
import { formatMoney, type CurrencyLike } from "./money";
import type { Transaction } from "./types";

export function describeReversal(tx: Transaction, refundFees: boolean, cur: CurrencyLike): string {
  const kind = (e: Transaction["entries"][number]) => e.kind ?? "principal";
  const principal = tx.entries.filter((e) => kind(e) === "principal");
  // Fees and levies credited to the bank's own accounts.
  const charges = tx.entries.filter((e) => kind(e) !== "principal" && e.side === "credit");
  const feeTotal = charges.reduce((n, e) => n + e.amount_minor, 0);
  const feeNames = [...new Set(charges.map((e) => e.description).filter(Boolean))].join(", ");
  const money = (n: number) => formatMoney(n, cur);

  if (tx.type === "fee") {
    const payer = tx.entries.find((e) => e.side === "debit")?.account_name ?? "the account";
    return `Refunds ${money(tx.amount_minor)}${feeNames ? ` (${feeNames})` : ""} to ${payer}.`;
  }
  const from = principal.find((e) => e.side === "debit")?.account_name;
  const to = principal.find((e) => e.side === "credit")?.account_name;
  let s = tx.type === "funding" && to
    ? `Takes back the ${money(tx.amount_minor)} paid to ${to}`
    : from && to
      ? `Returns ${money(tx.amount_minor)} to ${from} from ${to}`
      : `Moves ${money(tx.amount_minor)} back`;
  if (feeTotal > 0) {
    s += refundFees
      ? ` and refunds ${money(feeTotal)} in fees${feeNames ? ` (${feeNames})` : ""}.`
      : `. The bank keeps the ${money(feeTotal)} in fees${feeNames ? ` (${feeNames})` : ""}.`;
  } else {
    s += ".";
  }
  return s;
}
